import { useState } from "react";
import { useTranslation } from "react-i18next";
import AddCircleOutlinedIcon from "@mui/icons-material/AddCircleOutlined";
import InputAdornment from "@mui/material/InputAdornment";

import {
  Claim,
  MatchedClaim,
  MatchedSignalEventsOccurrence,
} from "shared/api/claims/api";
import { SignalEventsOccurrence } from "shared/api/signalEvents/api";
import { SHORT_DATE_FORMAT } from "shared/constants";
import { formatDate } from "shared/locale/format";
import { useClaimsSchema } from "shared/schemas/claimsSchema";
import useSignalEventOccurrencesSchema from "shared/schemas/signalEventOccurrencesSchema";
import { EventTypeEnum } from "shared/types";
import { randomID } from "shared/utils";

import { MATCHED_EVENTS_HOOK_MATCHING } from "pages/Dashboard/components/constants";
import { MatchedText } from "pages/Dashboard/components/EventDetails/ClaimDetails/MatchedText";
import { getFilteredData } from "pages/Dashboard/components/EventDetails/ClaimDetails/utils";
import { LandingPageEventType } from "pages/Dashboard/types";
import { RelatedEventGroup } from "pages/shared/Dashboard/types";

import Accordion from "features/components/Accordion";
import Button from "features/components/Button";
import Checkbox from "features/components/Checkbox";
import TextField from "features/components/TextField";
import Typography from "features/components/Typography";
import { DEFAULT_FILTER_BUILDER_STATE } from "features/ui/Filters/FilterBuilder/constants";
import {
  FilterGroupState,
  FilterRowState,
} from "features/ui/Filters/FilterBuilder/types";
import {
  getFiltersQuery,
  mergeFilterGroupStates,
} from "features/ui/Filters/FilterBuilder/utils";
import { useFilterSortState } from "features/ui/Filters/hooks";
import { FilterOperator } from "features/ui/Filters/types";
import SearchIcon from "features/ui/Icons/SearchIcon";
import MatchingTextHighlight from "features/ui/MatchingTextHighlight";
import Skeleton from "features/ui/Skeleton";

import { DEFAULT_RELEVANT_EVENTS_COUNT } from "./constants";

interface Props {
  config: RelatedEventGroup;
  vin: string;
  event: LandingPageEventType;
  eventType: EventTypeEnum;
}

const vinFilter = (vin: string): FilterRowState => ({
  id: `row-${randomID()}`,
  type: "row" as const,
  attribute: "VIN",
  operator: FilterOperator.IN,
  values: [vin],
});

const vinFilterState = (vin: string): FilterGroupState => ({
  id: `group-${randomID()}`,
  type: "group",
  anyAll: "all",
  children: [vinFilter(vin)],
});

const typeFilter = (signalEventType: string): FilterRowState => ({
  id: `row-${randomID()}`,
  type: "row" as const,
  attribute: "signalEventType",
  operator: FilterOperator.IN,
  values: [signalEventType],
});

const typeFilterState = (signalEventType: string): FilterGroupState => ({
  id: `group-${randomID()}`,
  type: "group",
  anyAll: "all",
  children: [typeFilter(signalEventType)],
});

const useRelatedEvents = ({ config, vin, event, eventType }: Props) => {
  const [showOnlyRelevantEvents, setShowOnlyRelevantEvents] = useState(true);
  const [shownItems, setShownItems] = useState(DEFAULT_RELEVANT_EVENTS_COUNT);

  const { attributeAccessors } = useSignalEventOccurrencesSchema();
  const { getDisplayLabel: getDisplayLabelEvents } =
    useSignalEventOccurrencesSchema();
  const { getDisplayLabel: getDisplayLabelClaims } = useClaimsSchema();

  const perPage = DEFAULT_RELEVANT_EVENTS_COUNT;

  const eventId =
    eventType === EventTypeEnum.CLAIM
      ? (event as Claim).ID
      : (event as SignalEventsOccurrence).signalEventID;

  const eventsFilterSortState = useFilterSortState({
    pageKey: `related-events-${config.title}-${eventId}`,
    defaultFilterValues:
      config.signalEventType && config.signalEventType.length > 0
        ? typeFilterState(config.signalEventType)
        : DEFAULT_FILTER_BUILDER_STATE,
    schemaAttributes: attributeAccessors,
    defaultSort: { [config.dateField]: "desc" },
    disableUsingLocalStorage: true,
    disableUsingQuery: true,
  });

  const filterQuery = getFiltersQuery(
    mergeFilterGroupStates(eventsFilterSortState.filters, vinFilterState(vin))
  );

  const groupBy =
    config.eventType == EventTypeEnum.SIGNAL_EVENT
      ? [...config.fields.flat(), config.titleField].join(",")
      : undefined;

  const hookKey = `${eventType}_${config.eventType}`;
  const hook = MATCHED_EVENTS_HOOK_MATCHING[hookKey];

  const hookParams =
    eventType === EventTypeEnum.SIGNAL_EVENT &&
    config.eventType === EventTypeEnum.CLAIM
      ? { filter: filterQuery, groupBy }
      : { id: (event as Claim).ID, filter: filterQuery, groupBy };

  const result = hook
    ? hook(hookParams as any)
    : { data: undefined, isLoading: false, error: false };

  const { data, isLoading, error } = result;

  const innerData = getFilteredData(data, showOnlyRelevantEvents);

  const onShownMore = () => {
    setShownItems(shownItems + perPage);
  };

  return {
    data: innerData,
    error,
    isLoading,
    onShownMore,
    shownItems,
    getDisplayLabelEvents,
    getDisplayLabelClaims,
    showOnlyRelevantEvents,
    setShowOnlyRelevantEvents,
    setShownItems,
  };
};

const EventRow = ({
  item,
  config,
  displayLabelFunction,
  onChange,
  expanded,
  matchedData,
  searchTerm,
}: {
  item: SignalEventsOccurrence | Claim;
  config: RelatedEventGroup;
  displayLabelFunction: (accessor: string, defaultLabel: string) => string;
  onChange: (event: React.SyntheticEvent, isExpanded: boolean) => void;
  expanded: boolean;
  matchedData?: Record<string, string[]> | null;
  searchTerm?: string;
}) => {
  const { t } = useTranslation();

  return (
    <Accordion
      onChange={onChange}
      expanded={expanded}
      variant="mixed"
      title={
        <div className="flex flex-row items-center">
          <Typography variant="body2" className="!mr-4">
            {formatDate(
              (item as SignalEventsOccurrence).recordedAt ||
                (item as Claim).date,
              SHORT_DATE_FORMAT
            )}
          </Typography>
          <Typography variant="h5" className="font-bold">
            <MatchingTextHighlight
              text={
                (item as any)?.[config.titleField]?.toString() ??
                t("common:not_available")
              }
              searchTerms={[]}
              secondarySearchTerms={searchTerm ? [searchTerm] : undefined}
            />
          </Typography>
          {(item as MatchedSignalEventsOccurrence).groupedCount !== undefined &&
            (item as MatchedSignalEventsOccurrence).groupedCount! > 1 && (
              <Typography variant="body2" className="!ml-4 text-gray-600">
                {t("schema:dashboard.occurrences_parenthetical", {
                  count: (item as MatchedSignalEventsOccurrence).groupedCount,
                })}
              </Typography>
            )}
        </div>
      }
    >
      <div className="flex flex-row">
        {config.fields.map((fieldGroup, index) => (
          <div key={index} className="flex flex-col gap-4 flex-1">
            {fieldGroup.map((field, index) => (
              <div key={index}>
                <div className="font-bold">
                  {displayLabelFunction(field, field)}
                </div>
                <div>
                  <MatchingTextHighlight
                    text={
                      (item as any)[field]?.toString() ??
                      t("common:not_available")
                    }
                    searchTerms={
                      matchedData?.[field] ? matchedData?.[field] : []
                    }
                    secondarySearchTerms={searchTerm ? [searchTerm] : undefined}
                  />
                </div>
              </div>
            ))}
          </div>
        ))}
      </div>
    </Accordion>
  );
};

const RelatedEvents = ({ config, vin, event, eventType }: Props) => {
  const {
    data,
    isLoading,
    getDisplayLabelEvents,
    getDisplayLabelClaims,
    onShownMore,
    shownItems,
    showOnlyRelevantEvents,
    setShowOnlyRelevantEvents,
    setShownItems,
  } = useRelatedEvents({
    config,
    vin,
    event,
    eventType,
  });
  const { t } = useTranslation();

  const [searchTerm, setSearchTerm] = useState("");
  const [expanded, setExpanded] = useState<number>(1);

  const selectedEvent =
    expanded && data && data.length > 0 ? data[expanded - 1] : undefined;

  const matchedData = selectedEvent
    ? (selectedEvent as MatchedSignalEventsOccurrence)
        .matchedSignalEventOccurrence?.matchingClaimDetails
      ? (selectedEvent as MatchedSignalEventsOccurrence)
          .matchedSignalEventOccurrence?.matchingClaimDetails
      : (selectedEvent as MatchedClaim).claimDetails
    : null;

  const noDataPlaceholder = (
    <div className="border rounded-lg border-gray-200 border-dashed h-60 flex flex-row items-center justify-center italic">
      <div>
        {searchTerm.trim().length > 0 && (
          <span>{t("schema:dashboard.no_matches")}</span>
        )}
        {searchTerm.trim().length === 0 && (
          <span>
            {!showOnlyRelevantEvents ? (
              t("schema:dashboard.no_events_prior_to_claim")
            ) : (
              <div className="flex flex-col gap-2 items-center">
                <div>
                  {t("schema:dashboard.no_relevant_events", {
                    title: config.title,
                  })}
                </div>
                <div
                  className="underline italic cursor-pointer"
                  onClick={() => setShowOnlyRelevantEvents(false)}
                >
                  {t("schema:dashboard.show_all_prior", {
                    title: config.title,
                  })}
                </div>
              </div>
            )}
          </span>
        )}
      </div>
    </div>
  );

  const viewTitle =
    config.title.length > 1
      ? config.title.charAt(0).toLowerCase() + config.title.slice(1)
      : config.title.toLowerCase();
  const showOnlyRelevantEventsLabel = t("schema:dashboard.only_show_relevant", {
    title: viewTitle,
  });

  const filteredData = data.filter((item) => {
    if (searchTerm && searchTerm.trim().length > 0) {
      return config.fields.flat().some((field) => {
        const value = (item as any)[field]?.toString() ?? "";

        return value.toLowerCase().includes(searchTerm.toLowerCase());
      });
    }

    return true;
  });

  return (
    <div className="text-xs">
      <div className="flex flex-col gap-1 bg-purple-50 border-b border-purple-300 p-2.5 pl-5 justify-between">
        <div className="font-bold text-sm">{config.title}</div>
        <Checkbox
          className="pl-2"
          onChange={() => setShowOnlyRelevantEvents(!showOnlyRelevantEvents)}
          checked={showOnlyRelevantEvents}
          label={showOnlyRelevantEventsLabel}
          size="small"
        />
      </div>

      <div className="p-4 flex flex-row gap-4">
        <div className="flex-row gap-4 flex-5">
          {data && (
            <>
              <div className="flex flex-col">
                <div className="flex flex-col gap-1">
                  <label className="text-xs pl-2 text-gray-700">
                    {t("schema:dashboard.keyword_search")}
                  </label>
                  <TextField
                    fullWidth
                    onChange={(e) => {
                      setSearchTerm(e.target.value);
                      setShownItems(DEFAULT_RELEVANT_EVENTS_COUNT);
                    }}
                    value={searchTerm}
                    hiddenLabel={true}
                    variant="outlined"
                    className="!mb-1.5"
                    slotProps={{
                      input: {
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon aria-hidden="true" />
                          </InputAdornment>
                        ),
                      },
                    }}
                  />
                </div>
                <div className="flex flex-col gap-1.5">
                  {filteredData.slice(0, shownItems).map((event, i) => (
                    <EventRow
                      expanded={expanded === i + 1} // using +1 to avoid 0 being false
                      onChange={() => setExpanded(i + 1)}
                      item={event}
                      key={`${(event as MatchedSignalEventsOccurrence).signalEventID ? (event as MatchedSignalEventsOccurrence).signalEventID : (event as MatchedClaim).ID}_${i}`}
                      config={config}
                      displayLabelFunction={
                        config.eventType === "claim"
                          ? getDisplayLabelClaims
                          : getDisplayLabelEvents
                      }
                      matchedData={(event as MatchedClaim).matchedClaimDetails}
                      searchTerm={searchTerm}
                    />
                  ))}
                </div>
                <div className="flex flex-col gap-3">
                  <div>
                    {isLoading && <Skeleton className="h-60" />}
                    {(!data || filteredData.length === 0) &&
                      !isLoading &&
                      noDataPlaceholder}
                  </div>

                  <div className="flex flex-row w-auto gap-2 h-10 justify-center items-center">
                    {!isLoading && filteredData.length > shownItems && (
                      <Button
                        color="secondary"
                        onClick={onShownMore}
                        variant="contained"
                        label={t("schema:dashboard.show_more_items", {
                          count: DEFAULT_RELEVANT_EVENTS_COUNT,
                        })}
                        endIcon={
                          <AddCircleOutlinedIcon sx={{ fontSize: 20 }} />
                        }
                      />
                    )}
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
        <MatchedText
          event={event}
          config={config}
          getDisplayLabel={getDisplayLabelClaims}
          matchedData={matchedData}
        />
      </div>
    </div>
  );
};

export default RelatedEvents;
